import React from "react";
import { X, Circle, ExternalLink } from "lucide-react";

// Read-only preview of a WhatsApp interactive message's own controls (list
// sections/rows, reply buttons, a Flow's launch button, or a location
// request) — per itemsecure-interactive-inbox-ui.md: "bot_response.messages[0]
// describes buttons, list, flow, or location_request" and "the customer
// completes the action in WhatsApp, not in the inbox." The tenant has nothing
// to click here; this just shows what the customer was offered.

export interface InteractiveListRow {
  id?: string;
  title: string;
  description?: string;
}

export interface InteractiveListSection {
  title?: string;
  rows: InteractiveListRow[];
}

export interface InteractivePreview {
  kind: "list" | "buttons" | "flow" | "location_request" | "flow_submission" | "unknown";
  headerText?: string;
  bodyText?: string;
  sections?: InteractiveListSection[];
  buttons?: string[];
  ctaText?: string;
  // "flow_submission" only — the real answers a customer gave on a WhatsApp
  // Flow (message_metadata.flow_response), not what they were offered.
  fields?: { label: string; value: string }[];
  // Overrides for template options ("Template options" + where they came from).
  title?: string;
  caption?: string;
}

interface InteractiveOptionsModalProps {
  isOpen: boolean;
  preview: InteractivePreview;
  onClose: () => void;
  // Only set for a Flow preview — real "Open form" launch, taking the tenant
  // on to the actual (static, pre-filled) Form Preview panel. Every other
  // kind here stays purely read-only, per the original spec.
  onOpenForm?: () => void;
}

export const InteractiveOptionsModal: React.FC<InteractiveOptionsModalProps> = ({ isOpen, preview, onClose, onOpenForm }) => {
  if (!isOpen) return null;

  const title =
    preview.title ||
    (preview.kind === "list"
      ? preview.headerText || "Choose one"
      : preview.kind === "buttons"
      ? "Quick replies"
      : preview.kind === "flow"
      ? "Form"
      : preview.kind === "flow_submission"
      ? preview.headerText || "Flow Submission"
      : "Share location");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 -mt-1 -mr-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {preview.bodyText && <p className="px-5 pb-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{preview.bodyText}</p>}

        <p className="px-5 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {preview.caption || (preview.kind === "flow_submission" ? "What the customer actually submitted" : "What the customer could select — not clickable here")}
        </p>

        <div className="px-2 pb-4">
          {preview.kind === "flow_submission" &&
            (preview.fields || []).map((f, i) => (
              <div key={i} className="flex items-start justify-between gap-3 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">{f.label}</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 text-right break-words">{f.value}</span>
              </div>
            ))}
          {preview.kind === "flow_submission" && (preview.fields || []).length === 0 && (
            <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">No submitted values recorded for this message.</p>
          )}
          {preview.kind === "list" &&
            (preview.sections || []).map((section, si) => (
              <div key={si} className="px-3 pt-2">
                {section.title && <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1 px-0.5">{section.title}</p>}
                {section.rows.map((row, ri) => (
                  <div key={row.id || ri} className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{row.title}</p>
                      {row.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{row.description}</p>}
                    </div>
                    <Circle size={18} className="text-gray-300 dark:text-gray-600 shrink-0" />
                  </div>
                ))}
              </div>
            ))}

          {preview.kind === "buttons" &&
            (preview.buttons || []).map((label, i) => (
              <div key={i} className="mx-3 my-1.5 py-2.5 px-3 text-center text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 rounded-lg">
                {label}
              </div>
            ))}

          {preview.kind === "flow" && onOpenForm ? (
            <button
              type="button"
              onClick={onOpenForm}
              className="w-[calc(100%-1.5rem)] mx-3 my-1.5 flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 rounded-lg bg-transparent cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
            >
              <ExternalLink size={14} /> {preview.ctaText || "Open form"}
            </button>
          ) : (
            (preview.kind === "flow" || preview.kind === "location_request") && (
              <div className="mx-3 my-1.5 flex items-center justify-center gap-1.5 py-2.5 px-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 rounded-lg">
                <ExternalLink size={14} /> {preview.ctaText}
              </div>
            )
          )}

          {preview.kind === "list" && !(preview.sections || []).some((s) => s.rows.length > 0) && (
            <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">No options recorded for this message.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveOptionsModal;
